/**
 * Tradução da forma crua da API para o domínio da tela de glosas (`TP-054`,
 * T11). Roda UMA vez, logo depois do fetch — ver `api/tipos.ts`.
 *
 * Os tokens já vêm em pt-BR; o que se faz aqui é fechar o vocabulário: um
 * token novo que a API mandar vira `desconhecido`, nunca um tipo válido por
 * engano (mesma disciplina de `mapearEstadoDaLinha` no faturamento).
 */

import {
  AREAS_DE_GLOSA,
  ROTULOS_DE_CAUSA,
  ROTULOS_DE_MOTIVO,
  TIPOS_DE_GLOSA,
  type AcaoDaDecisao,
  type AreaDeGlosa,
  type Confianca,
  type DemonstrativoApi,
  type DemonstrativoImportado,
  type Glosa,
  type GlosaApi,
  type MotivoDeNaoRotear,
  type OrigemDaCausa,
  type RoteamentoApi,
  type RoteamentoDaGlosa,
  type TipoDeGlosa,
  type TrechoCitado,
} from './tipos';

function ehTipo(bruto: string | null): bruto is TipoDeGlosa {
  return bruto !== null && (TIPOS_DE_GLOSA as readonly string[]).includes(bruto);
}

const CONFIANCAS: readonly Confianca[] = ['alta', 'media', 'baixa'];
const ORIGENS: readonly OrigemDaCausa[] = ['codigo_operadora', 'verificacao_motor', 'sem_causa'];
const ACOES: readonly AcaoDaDecisao[] = ['confirmar', 'corrigir'];

function mapearConfianca(bruto: string): Confianca {
  return (CONFIANCAS as readonly string[]).includes(bruto) ? (bruto as Confianca) : 'desconhecido';
}

function mapearOrigem(bruto: string): OrigemDaCausa {
  return (ORIGENS as readonly string[]).includes(bruto) ? (bruto as OrigemDaCausa) : 'desconhecido';
}

function mapearAcao(bruto: string | null): AcaoDaDecisao | null {
  return bruto !== null && (ACOES as readonly string[]).includes(bruto) ? (bruto as AcaoDaDecisao) : null;
}

/** Causa fora do vocabulário aparece crua: melhor o token do que esconder a causa. */
function rotularCausa(bruto: string | null): string | null {
  if (bruto === null) return null;
  return ROTULOS_DE_CAUSA[bruto] ?? bruto;
}

function mapearArea(bruto: string): AreaDeGlosa | 'desconhecida' {
  return (AREAS_DE_GLOSA as readonly string[]).includes(bruto) ? (bruto as AreaDeGlosa) : 'desconhecida';
}

function mapearMotivo(bruto: string): MotivoDeNaoRotear {
  return bruto in ROTULOS_DE_MOTIVO ? (bruto as MotivoDeNaoRotear) : 'desconhecido';
}

/**
 * Fecha o roteamento no vocabulário (`TP-070`). Ticket vence motivo (a API nunca manda os dois). Tudo vazio é a linha
 * elegível sem ticket ainda; sem o campo, a API é anterior ao roteamento.
 */
export function mapearRoteamento(bruto: RoteamentoApi | null | undefined): RoteamentoDaGlosa | null {
  if (bruto === null || bruto === undefined) return null;
  if (bruto.ticketId !== null && bruto.area !== null) {
    return {
      situacao: 'roteada',
      area: mapearArea(bruto.area),
      ticketId: bruto.ticketId,
      ticketEstado: bruto.ticketEstado ?? 'desconhecido',
      prazoEm: bruto.prazoEm,
    };
  }
  if (bruto.motivo !== null) return { situacao: 'nao_roteada', motivo: mapearMotivo(bruto.motivo) };
  return { situacao: 'pendente' };
}

function trechoCitado(trecho: string | null | undefined, pagina: number | null | undefined): TrechoCitado | null {
  return trecho !== null && trecho !== undefined && trecho.trim() !== '' ? { trecho, pagina: pagina ?? null } : null;
}

export function mapearGlosa(bruto: GlosaApi): Glosa {
  return {
    id: bruto.id,
    demonstrativoId: bruto.demonstrativoId,
    linha: bruto.linha,
    guia: bruto.guia,
    codigo: bruto.codigo,
    protocolo: bruto.protocolo ?? null,
    valorGlosado: bruto.valorGlosado,
    dataPagamento: bruto.dataPagamento,
    motivoCodigo: bruto.motivoCodigo,
    causa: rotularCausa(bruto.causa),
    tipo: ehTipo(bruto.tipo) ? bruto.tipo : 'desconhecido',
    confianca: mapearConfianca(bruto.confianca),
    origemDaCausa: mapearOrigem(bruto.decididaPor),
    verificacao: bruto.verificacao,
    clausula: trechoCitado(bruto.hipoteseTrecho, bruto.hipotesePagina),
    regraConferida: trechoCitado(bruto.regraConferidaTrecho, bruto.regraConferidaPagina),
    prazoRecursoAte: bruto.prazoRecursoAte,
    clausulaDoPrazo: trechoCitado(bruto.prazoRecursoTrecho, bruto.prazoRecursoPagina),
    ultimaDecisao: mapearAcao(bruto.ultimaDecisao),
    tipoCorrigido: ehTipo(bruto.tipoCorrigido) ? bruto.tipoCorrigido : null,
    roteamento: mapearRoteamento(bruto.roteamento),
  };
}

export function mapearDemonstrativo(bruto: DemonstrativoApi): DemonstrativoImportado {
  return {
    demonstrativoId: bruto.demonstrativoId,
    linhas: bruto.linhas,
    confiancaBaixa: bruto.confiancaBaixa,
    semPar: bruto.semPar,
  };
}

/** O tipo que vale hoje: a correção humana, quando houver, senão o do classificador. */
export function tipoVigente(glosa: Glosa): TipoDeGlosa | 'desconhecido' {
  return glosa.ultimaDecisao === 'corrigir' && glosa.tipoCorrigido !== null ? glosa.tipoCorrigido : glosa.tipo;
}

/**
 * Dias corridos de hoje até o prazo (`AAAA-MM-DD`, sem hora). Negativo = vencido.
 * As duas pontas são lidas como data de calendário em UTC, então fuso e horário de
 * verão não mudam a conta.
 */
export function diasAteOPrazo(prazo: string, hoje: Date = new Date()): number | null {
  const achado = /^(\d{4})-(\d{2})-(\d{2})$/.exec(prazo);
  if (achado === null) return null;
  const alvo = Date.UTC(Number(achado[1]), Number(achado[2]) - 1, Number(achado[3]));
  const base = Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return Math.round((alvo - base) / 86_400_000);
}

/**
 * O dia do prazo (`AAAA-MM-DD`) de um instante ISO, lido em Brasília: o prazo do recurso é uma data, e o ticket guarda o
 * fim desse dia. `null` para texto que não é um instante.
 */
export function diaDoPrazoEm(prazoEm: string): string | null {
  const instante = new Date(prazoEm);
  if (Number.isNaN(instante.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    instante,
  );
}

/** `2026-11-30` → `30/11/2026`. Texto fora do formato volta como veio. */
export function formatarDataDoPrazo(prazo: string): string {
  const achado = /^(\d{4})-(\d{2})-(\d{2})$/.exec(prazo);
  return achado === null ? prazo : `${achado[3] ?? ''}/${achado[2] ?? ''}/${achado[1] ?? ''}`;
}

/** O item glosado como a tela mostra: o código, ou "guia inteira" quando a glosa vem sem item (D13). */
export function itemDaGlosa(glosa: Glosa): string {
  return glosa.codigo === null ? 'guia inteira' : `código ${glosa.codigo}`;
}
